
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, MessageSquare, Users } from 'lucide-react';
import { useUserTeams } from '@/hooks/useUserRelationships';
import { useGames } from '@/hooks/useGames';
import { format } from 'date-fns';
import { LoyaltyBonusDisplay } from '@/components/retention/LoyaltyBonusDisplay';
import MinorAgentRulesCard from '@/components/dashboard/MinorAgentRulesCard';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useAuth } from '@/hooks/useAuth';
import { useChildParentConnectionStatus } from '@/hooks/useChildParentConnectionStatus';

const PlayerDashboard: React.FC = () => {
  const { user: internalUser, profile: internalProfile } = useInternalAuth();
  const { profile: supabaseProfile } = useAuth();
  
  // Determine if user is a minor (under 18)
  const isMinor = React.useMemo(() => {
    // Check internal user first
    if (internalUser?.age_group) {
      return internalUser.age_group === '13-and-under' || 
             internalUser.age_group === '14-17' ||
             (parseInt(internalUser.age_group) <= 17 && !isNaN(parseInt(internalUser.age_group)));
    }
    // Check internal profile
    if (internalProfile?.age_group) {
      return internalProfile.age_group === '13-and-under' || 
             internalProfile.age_group === '14-17';
    }
    if (internalProfile?.age && internalProfile.age < 18) {
      return true;
    }
    // Check supabase profile
    if (supabaseProfile?.age_group) {
      return supabaseProfile.age_group === '13-and-under' || 
             supabaseProfile.age_group === '14-17';
    }
    // Default to treating unknown age as minor for safety (for internal users only)
    return internalUser ? true : false;
  }, [internalUser, internalProfile, supabaseProfile]);

  // Check parent connection status for minors
  const { hasParentConnected } = useChildParentConnectionStatus(internalUser?.id);

  const formatGameTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      
      const formattedTime = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      
      return `${formattedTime} CST`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return time;
    }
  };

  const { userTeams, loading: teamsLoading } = useUserTeams();
  const { userGames, loading: gamesLoading } = useGames();

  // Filter games for this week
  const thisWeekGames = userGames.filter(game => {
    const gameDate = new Date(game.game_date);
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return gameDate >= now && gameDate <= weekFromNow;
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/games" className="block">
          <Card className="bg-gray-800 border-gray-700 hover:border-sports-green transition-colors cursor-pointer">
            <CardContent className="p-4 flex items-center space-x-4">
              <Calendar className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Upcoming Games</h4>
                <p className="text-sm text-gray-300">
                  {gamesLoading ? 'Loading...' : `${thisWeekGames.length} this week`}
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/chat" className="block">
          <Card className="bg-gray-800 border-gray-700 hover:border-sports-green transition-colors cursor-pointer">
            <CardContent className="p-4 flex items-center space-x-4">
              <MessageSquare className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Team Messages</h4>
                <p className="text-sm text-gray-300">Check chat for updates</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/teams" className="block">
          <Card className="bg-gray-800 border-gray-700 hover:border-sports-green transition-colors cursor-pointer">
            <CardContent className="p-4 flex items-center space-x-4">
              <Users className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">My Teams</h4>
                <p className="text-sm text-gray-300">
                  {teamsLoading ? 'Loading...' : `${userTeams.length} active`}
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-4">
          <h4 className="font-semibold mb-2 text-white">This Week's Schedule</h4>
          <div className="space-y-2">
            {gamesLoading ? (
              <div className="text-gray-400 text-center">Loading schedule...</div>
            ) : thisWeekGames.length > 0 ? (
              thisWeekGames.slice(0, 3).map((game) => {
                const gameDate = new Date(game.game_date);
                const gameTime = game.game_time ? new Date(`1970-01-01T${game.game_time}`) : null;
                
                return (
                  <div key={game.id} className="p-3 bg-gray-700 rounded-md border border-gray-600">
                    <div className="flex justify-between">
                      <span className="font-medium text-white">
                        {game.game_name}
                      </span>
                      <span className="text-gray-300">
                        {format(gameDate, 'EEE')}, {gameTime ? format(gameTime, 'h:mm a') + ' CST' : formatGameTime(game.game_time)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-300">
                      {game.venue_name || 'TBD'}
                    </p>
                  </div>
                );
              })
            ) : (
              <div className="p-3 bg-gray-700 rounded-md border border-gray-600">
                <div className="text-center text-gray-400">
                  No games scheduled this week
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Minor Agent Rules Card - only show for minors */}
      {isMinor && (
        <MinorAgentRulesCard hasParentConnected={hasParentConnected} className="mt-4" />
      )}

      <LoyaltyBonusDisplay />
    </div>
  );
};

export default PlayerDashboard;
